/**
 * Les points de rendez-vous de la page — ancres du menu, et le chemin de l'aide.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI UN FICHIER, ET POURQUOI PAS DANS `publics.ts`
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `publics.ts` porte le CONTRAT entre la vitrine et le lot « lieux libres et
 * événements ponctuels » : son en-tête documente les deux seules lignes qu'un
 * lot voisin a le droit d'y changer. Y déverser les ancres du menu diluerait ce
 * contrat dans de la mise en page, et la prochaine personne à l'ouvrir devrait
 * relire quarante lignes pour retrouver les deux qui l'intéressent.
 *
 * D'où ce fichier-ci, qui ne contient que des identifiants d'ancre — et une
 * règle simple : **l'ancre est écrite ici, jamais dans deux endroits**. Un
 * `href="#titre-tarifs"` recopié dans l'en-tête et un `id="titre-tarifs"` dans
 * la section sont deux chaînes qui divergeront le jour où l'une bouge, et le
 * lien ne fera rien du tout — sans qu'aucune erreur ne le dise.
 *
 * ★ Chaque constante est employée DEUX FOIS : une fois par le lien qui y mène,
 *   une fois par la section qui la porte. C'est cette double consommation qui
 *   rend le garde-fou de `vitrine.test.tsx` possible — il vérifie que toute
 *   ancre citée dans un `href` existe bien comme `id` dans le rendu.
 */

/* -------------------------------------------------------------------------- */
/* Les ancres du menu                                                         */
/* -------------------------------------------------------------------------- */

/** Section 2 — « De la salle vide au soir de première ». */
export const ID_COUVERTURE = 'titre-couverture'

/** Section 3 — la campagne de réabonnement, le différenciateur. */
export const ID_CAMPAGNE = 'titre-campagne'

/** Section 4 — la simplicité, prouvée. Le cœur de la commande. */
export const ID_SIMPLICITE = 'titre-simplicite'

/** Section 5 — la boutique publique. */
export const ID_BOUTIQUE = 'titre-billetterie'

/** Section 7 — les documents que reçoivent les spectateurs. */
export const ID_DOCUMENTS = 'titre-documents'

/** Section 8 — le contrôle d'accès. */
export const ID_SCAN = 'titre-scan'

/** Section 9 — l'espace abonné. */
export const ID_PORTAIL = 'titre-portail'

/** Section 10 — les garanties et les chiffres. */
export const ID_GARANTIES = 'titre-garanties'

/** Section 11 — l'événement ponctuel, où la bande d'aiguillage dépose son lecteur. */
export const ID_EVENEMENT = 'titre-evenement'

/** Section 12 — le tarif. */
export const ID_TARIFS = 'titre-tarifs'

/** Section 13 — ce que le logiciel ne fait pas. Citée depuis trois sections. */
export const ID_HONNETE = 'titre-honnete'

/** Section 14 — l'éditeur. */
export const ID_EDITEUR = 'titre-editeur'

/**
 * Section 15 — le formulaire de contact.
 *
 * ⚠️ Son `id` est posé par `SectionContact.tsx`, qui n'est PAS touché par la
 * refonte (c'est le fichier le plus rigoureux de la vitrine : vrai formulaire
 * plutôt que `mailto`, champ piège, mesure de durée en horloge monotone,
 * sortie de secours si l'envoi échoue). La constante est donc ici pour les
 * LIENS qui y mènent, et sa valeur doit rester alignée sur celle du fichier —
 * un test le vérifie.
 */
export const ID_CONTACT = 'titre-contact'

/** Section 16 — les trois façons d'aller plus loin. */
export const ID_PORTES = 'titre-portes'

/** Un `id` de section, tel qu'un `href` doit l'écrire. */
export function ancre(id: string): string {
  return `#${id}`
}

/* -------------------------------------------------------------------------- */
/* La documentation                                                           */
/* -------------------------------------------------------------------------- */

/**
 * `/aide` — le sommaire de la documentation (chantier L).
 *
 * ★ **Écrit ici plutôt qu'importé de `features/aide`**, pour la même raison que
 * `CHEMIN_ESSAI` dans `publics.ts` : ARCHITECTURE §9.2, aucune feature n'importe
 * une autre feature. Le jumeau qui fait foi est `CHEMIN_AIDE`, exporté par
 * `features/aide/modele.ts` et consommé par le routeur. Deux constantes, un
 * seul chemin — et un test qui échoue si elles divergent.
 *
 * ★★ **La condition pour que ce lien parte** (RESTE-A-FAIRE §R, « le lien vers
 * la documentation ») : `/aide` doit être servi depuis l'apex COMME depuis le
 * sous-domaine d'une salle, et sans garde de session. C'est le cas — le routeur
 * monte `routesAide` à la racine, hors de toute garde, à côté de la boutique et
 * de l'inscription. Vérifié le 22.08.2026 ; un test le revérifie à chaque
 * exécution, pour qu'un menu ne promette jamais un manuel qui rendrait un 404.
 */
export const CHEMIN_AIDE_VITRINE = '/aide'

export function lienAideVitrine(): string {
  return /^(?:www\.)?ridomio\.fr$/iu.test(globalThis.location?.hostname ?? '')
    ? 'https://www.ridomio.ch/aide'
    : CHEMIN_AIDE_VITRINE
}
